import { useEffect, useState, useRef } from "react";
import { HeartHandshake, ShieldPlus, PawPrint } from "lucide-react";
import IllustrationBenevole from "../images/DA_Savoinimaux/Illustration-bénévole-Savoinimaux.webp";

export default function OurMissionSection() {
  const TARGET_COUNT = 1050;
  const [savedCount, setSavedCount] = useState(TARGET_COUNT);
  const sectionRef = useRef(null);
  const hasStarted = useRef(false);

  const missions = [
    {
      title: "Soins et réhabilitation",
      text: "Prise en charge médicale, remise en confiance et accompagnement progressif vers un meilleur équilibre.",
      icon: ShieldPlus,
    },
    {
      title: "Sauvetage d'urgence",
      text: "Intervention rapide pour les animaux abandonnés, blessés ou en situation de détresse.",
      icon: HeartHandshake,
    },
  ];

  useEffect(() => {
    if (!sectionRef.current || !("IntersectionObserver" in window)) {
      setSavedCount(TARGET_COUNT);
      return;
    }

    const startAnimation = () => {
      if (hasStarted.current) return;
      hasStarted.current = true;

      let current = 0;
      setSavedCount(0);

      const interval = setInterval(() => {
        current += 10;

        if (current >= TARGET_COUNT) {
          setSavedCount(TARGET_COUNT);
          clearInterval(interval);
          return;
        }

        setSavedCount(current);
      }, 12);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startAnimation();
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(sectionRef.current);

    const fallback = setTimeout(() => {
      startAnimation();
    }, 1200);

    return () => {
      clearTimeout(fallback);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.92fr]">
          <div className="relative">
            <div className="absolute -left-8 top-10 hidden h-36 w-36 rounded-full bg-red-100 blur-3xl xl:block" />
            <div className="absolute -right-8 bottom-10 hidden h-36 w-36 rounded-full bg-orange-100 blur-3xl xl:block" />

            <div className="relative overflow-hidden rounded-[2.5rem] border border-black/6 bg-[#6fd0f0] shadow-[0_30px_100px_rgba(0,0,0,0.08)]">
              <img
                src={IllustrationBenevole}
                alt="Illustration bénévole Savoinimaux"
                className="h-full max-h-[820px] min-h-[520px] w-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="absolute -bottom-10 left-[-22px] max-w-[calc(100vw-32px)] rounded-[2rem] border border-black/6 bg-white px-6 py-6 shadow-[0_25px_80px_rgba(0,0,0,0.08)] md:left-[-40px] md:px-8 md:py-7">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                  <PawPrint className="h-7 w-7" />
                </div>

                <div>
                  <p className="text-4xl font-semibold leading-none text-red-600 md:text-5xl">
                    {savedCount}+
                  </p>
                  <p className="mt-3 max-w-[220px] text-base leading-7 text-black/65">
                    Animaux sauvés depuis notre création en Savoie.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.35em] text-red-600">
              Notre mission
            </p>

            <h2 className="mt-4 text-4xl font-semibold leading-[1.02] text-[#111111] md:text-6xl">
              Un engagement sans faille
              <span className="block">pour nos amis à poils.</span>
            </h2>

            <p className="mt-8 max-w-2xl text-base leading-8 text-black/62 md:text-lg">
              Savoinimaux naît d’une conviction profonde : aucun animal ne
              devrait souffrir de l’indifférence. L’association agit en Savoie
              pour secourir, soigner et accompagner les animaux en détresse,
              tout en sensibilisant le public à la cause animale.
            </p>

            <div className="mt-12 space-y-8">
              {missions.map((item) => {
                const Icon = item.icon;

                return (
                  <div key={item.title} className="flex items-start gap-5">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600 shadow-[0_10px_30px_rgba(220,38,38,0.08)]">
                      <Icon className="h-7 w-7" />
                    </div>

                    <div>
                      <h3 className="text-2xl font-semibold text-[#111111]">
                        {item.title}
                      </h3>
                      <p className="mt-2 max-w-xl text-base leading-8 text-black/60">
                        {item.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}